// React
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

// Services & API
import { getVehicleServiceAPI } from "services/api/calculations/vehicleservice/api";

// Constants
import { initialVehicleServicesDetailForm } from "services/api/calculations/vehicleservice/consts";
import { CALCULATION_VEHICLE_SERVICES_DETAIL_PAGE_TITLE } from "ui/components/PageTitle/consts";
import { SIDEBAR_ITEM_CALCULATION_VEHICLE_SERVICES } from "app/components/Sidebar/consts";

// Types
import { VehicleServicesDetailFormValues } from "services/api/calculations/vehicleservice/types";
import { Errors } from "services/forms/validation/types";
import {
  VehicleServicesDetailPageCmp,
  VehicleServicesDetailPageProps,
} from "./types";

// UI Components
import { BreadcrumbItem } from "ui/components/Breadcrumb/types";
import { Modal } from "ui/components/Modal";
import { PageHeader } from "ui/components/PageHeader";
import { PageTitle } from "ui/components/PageTitle";
import { Breadcrumb } from "ui/components/Breadcrumb";
import { Button } from "ui/components/Button/Button";
import ModalBody from "ui/components/Modal/ModalBody";
import ModalHeader from "ui/components/Modal/ModalHeader";

// Helpers
import { transformRoute, useTranslate } from "helpers";
import { Routes } from "ui/modules/calculation/navigation";

// Components
import VehicleServicesDetailCard from "./components/VehicleServicesDetailCard/VehicleServicesDetailCard";
import VehicleServicesDeleteCard from "./components/VehicleServicesDeleteCard/VehicleServicesDeleteCard";

const VehicleServicesDetailPage: VehicleServicesDetailPageCmp = (
  props: VehicleServicesDetailPageProps
) => {
  const { vehicleServicesId } = useParams();

  const { t } = useTranslate();
  const [errors, setErrors] = useState<Errors>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);

  const [formValues, setFormValues] = useState<VehicleServicesDetailFormValues>(
    initialVehicleServicesDetailForm
  );

  const navigate = useNavigate();

  const title = CALCULATION_VEHICLE_SERVICES_DETAIL_PAGE_TITLE;

  useEffect(() => {
    if (vehicleServicesId) fetchVehicleServices(vehicleServicesId);
  }, [vehicleServicesId]);

  const fetchVehicleServices = async (id: string) => {
    if (id === undefined) {
      setFormValues(initialVehicleServicesDetailForm);
      return;
    }

    setIsLoading(true);

    let result;
    try {
      result = await getVehicleServiceAPI(id);
    } catch {
      setIsLoading(false);
      return;
    }

    setFormValues(result);
    setIsLoading(false);

    setErrors({});
  };

  const breadcrumbs: BreadcrumbItem[] = [
    {
      text: SIDEBAR_ITEM_CALCULATION_VEHICLE_SERVICES.text,
      route: SIDEBAR_ITEM_CALCULATION_VEHICLE_SERVICES.route,
    },
  ];

  const handleDeleteSuccess = useCallback(() => {
    navigate(
      transformRoute(Routes.CalculationVehicleServicesSearchPageRouter, {
        vehicleServicesId,
      })
    );
  }, []);

  if (!vehicleServicesId) return <></>;

  return (
    <>
      <div className="">
        <div className="bg-white border-b border-slate-200 rounded-t-md">
          <PageHeader>
            <PageTitle
              title={`${title.text}`}
              icon={title.icon}
              iconColor={title.iconColor}
            />
            <div className="flex justify-between">
              <Breadcrumb items={breadcrumbs} />
              <div className="flex gap-2">
                <Button
                  size="large"
                  variant="remove"
                  children={t(
                    "admin.calculation.setting.vehicleservices.detail.delete"
                  )}
                  onClick={() => setShowDeleteModal(true)}
                  startIcon="/media/icons/trash.svg"
                />
              </div>
            </div>
          </PageHeader>
        </div>
        <div className="px-4 py-6  lg:px-4">
          <VehicleServicesDetailCard vehicleServicesId={vehicleServicesId} />
        </div>
      </div>
      {showDeleteModal && (
        <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
          <ModalHeader
            title={t("admin.calculation.setting.vehicleservices.delete.title")}
            onHide={() => setShowDeleteModal(false)}
          />
          <ModalBody>
            <VehicleServicesDeleteCard
              vehicleServicesId={vehicleServicesId}
              onSuccess={handleDeleteSuccess}
            />
          </ModalBody>
        </Modal>
      )}
    </>
  );
};

VehicleServicesDetailPage.route = Routes.CalculationVehicleServicesDetailPageRouter;

export default VehicleServicesDetailPage;
